import React from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/_components/select";
import { ContactInfo, RecipientsResponse, TransMasterData, RecipientItem } from "../../types";
import { useContactSelection } from "./hooks/useContactSelection";

interface PhoneSelectorProps {
  contactInfo: ContactInfo;
  setContactInfo: React.Dispatch<React.SetStateAction<ContactInfo>>;
  recipients: RecipientsResponse;
  transMasterData: TransMasterData | null;
  setSelectedCandidate: React.Dispatch<React.SetStateAction<RecipientItem | null>>;
}

export const PhoneSelector: React.FC<PhoneSelectorProps> = ({
  contactInfo,
  setContactInfo,
  recipients,
  setSelectedCandidate
}) => {
  const { handleContactSelection, removeSelection } = useContactSelection({
    contactInfo,
    setContactInfo,
    setSelectedCandidate
  });

  if (recipients.phoneNos.length === 0) {
    return null;
  }

  const isDisabled = !!contactInfo.email;

  const handleValueChange = (value: string) => {
    if (value === "remove") {
      removeSelection("phone");
    } else {
      const recipient = recipients.phoneNos.find(item => item.mobilePhoneNo === value);
      handleContactSelection("phone", value, recipient);
    }
  };

  const getLabelText = () => {
    let text = "Phone Number";
    if (contactInfo.phone) text += " (Selected)";
    if (isDisabled) text += " (Disabled - Email selected)";
    return text;
  };

  const getSelectClassName = () => {
    if (isDisabled) {
      return "border-muted bg-muted text-muted-foreground cursor-not-allowed";
    }
    if (contactInfo.phone) {
      return "border-green-500 bg-green-50 dark:bg-green-950 dark:border-green-400";
    }
    return "";
  };

  return (
    <div>
      <label 
        htmlFor="phone-select" 
        className={`text-sm font-medium ${isDisabled ? 'text-muted-foreground' : 'text-foreground'}`}
      >
        {getLabelText()}
      </label>
      
      <Select
        key={`phone-${contactInfo.phone}-${contactInfo.email}`}
        value={contactInfo.phone || undefined}
        onValueChange={handleValueChange}
        disabled={isDisabled}
      >
        <SelectTrigger className={`mt-1 ${getSelectClassName()}`}>
          <SelectValue
            placeholder={isDisabled ? "Disabled" : "Select a phone number"}
          />
        </SelectTrigger>
        
        <SelectContent>
          {contactInfo.phone && (
            <>
              <SelectItem value="remove" className="text-red-600 dark:text-red-400 font-medium">
                ❌ Remove Phone Selection
              </SelectItem>
              <div className="border-t border-border my-1"></div>
            </>
          )}
          {recipients.phoneNos
            .filter((item): item is typeof item & { mobilePhoneNo: string } => 
              !!item.mobilePhoneNo && item.mobilePhoneNo.trim() !== ""
            )
            .map((item, index) => (
              <SelectItem 
                key={index} 
                value={item.mobilePhoneNo}
              >
                {item.mobilePhoneNo} ({item.fullName} - {item.recipientId})
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    </div>
  );
};